import React, { useEffect, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';

interface LiveRefreshCountdownProps {
  /** Epoch ms when the next live fetch is scheduled. Hidden when null. */
  nextFetchAt: number | null;
}

function formatRemaining(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Reverse counter for live log reload. Counts down to the next automatic fetch.
 */
export const LiveRefreshCountdown: React.FC<LiveRefreshCountdownProps> = React.memo(({ nextFetchAt }) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (nextFetchAt == null) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, [nextFetchAt]);

  if (nextFetchAt == null) return null;

  const remainingSeconds = Math.max(0, Math.ceil((nextFetchAt - now) / 1000));
  const label = formatRemaining(remainingSeconds);
  const tooltip = remainingSeconds === 0
    ? 'Fetching new logs now'
    : `New logs will be fetched automatically in ${label}`;

  return (
    <Tooltip title={tooltip}>
      <Box
        role="timer"
        aria-label={tooltip}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1,
          height: 32,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'success.main',
          color: 'success.main',
          backgroundColor: (theme) => theme.palette.mode === 'dark'
            ? 'rgba(76, 175, 80, 0.12)'
            : 'rgba(76, 175, 80, 0.08)',
          flexShrink: 0,
          userSelect: 'none',
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1, letterSpacing: 0.2 }}>
          Next
        </Typography>
        <Typography
          variant="caption"
          component="span"
          sx={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            fontWeight: 700,
            fontSize: '0.8rem',
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
            minWidth: '2.75em',
          }}
        >
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
});

LiveRefreshCountdown.displayName = 'LiveRefreshCountdown';
